import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  is_anonymous: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  sign_in_anonymously: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const is_anonymous = user?.is_anonymous ?? false;

  const syncProfileFromAuth = async (nextUser: User | null) => {
    if (!nextUser) return;

    const fullName = String(
      nextUser.user_metadata?.full_name ??
      nextUser.user_metadata?.name ??
      ''
    ).trim();
    const oauthAvatar = String(
      nextUser.user_metadata?.avatar_url ??
      nextUser.user_metadata?.picture ??
      ''
    ).trim();

    const { data: current, error: selectError } = await supabase
      .from('profiles')
      .select('full_name, avatar_url')
      .eq('id', nextUser.id)
      .maybeSingle();

    if (selectError) return;

    if (!current) {
      await supabase.from('profiles').upsert(
        {
          id: nextUser.id,
          full_name: fullName || null,
          avatar_url: oauthAvatar || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' }
      );
      return;
    }

    const updates: { full_name?: string | null; avatar_url?: string | null; updated_at?: string } = {};
    if (!current.full_name && fullName) updates.full_name = fullName;
    if (!current.avatar_url && oauthAvatar) updates.avatar_url = oauthAvatar;
    if (Object.keys(updates).length === 0) return;
    updates.updated_at = new Date().toISOString();

    await supabase.from('profiles').update(updates).eq('id', nextUser.id);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser((prev) => {
        const next = session?.user ?? null;
        if (prev?.id === next?.id && prev?.updated_at === next?.updated_at) {
          return prev;
        }
        return next;
      });
      setLoading(false);

      // PostHog: identificar ou resetar usuario (carregamento assincrono)
      import('posthog-js').then(({ default: posthog }) => {
        if (session?.user) {
          posthog.identify(session.user.id, {
            email: session.user.email,
            name: session.user.user_metadata?.full_name ?? '',
          });
        } else {
          posthog.reset();
        }
      }).catch(() => { /* posthog indisponivel */ });
      void syncProfileFromAuth(session?.user ?? null);

      // Envia email de boas-vindas apenas no primeiro cadastro (nao para anonimos)
      if (event === 'SIGNED_IN' && session?.user && !session.user.is_anonymous) {
        const created_at = new Date(session.user.created_at).getTime();
        const agora = Date.now();
        if (agora - created_at < 30_000) {
          void supabase.functions.invoke('send-transactional-email', {
            body: {
              templateName: 'welcome',
              recipientEmail: session.user.email,
              idempotencyKey: `welcome-${session.user.id}`,
              templateData: {
                name: String(session.user.user_metadata?.full_name ?? '').trim() || undefined,
              },
            },
          });
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser((prev) => {
        const next = session?.user ?? null;
        if (prev?.id === next?.id && prev?.updated_at === next?.updated_at) {
          return prev;
        }
        return next;
      });
      setLoading(false);
      void syncProfileFromAuth(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const sign_in_anonymously = async () => {
    const { error } = await supabase.auth.signInAnonymously();
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, is_anonymous, signUp, signIn, sign_in_anonymously, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
